// hooks/useListProcessor.js
import { useState, useCallback, useEffect } from "react";
import { processLists } from "../../utils/listProcessor";
import { useToast } from "../../contexts/ToastContext";

export const useListProcessor = (options = {}) => {
  const { showAlerts = true, autoSave = false, onSuccess, onError } = options;
  const { toast } = useToast();

  // State for hook
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({
    totalProcessed: 0,
    totalListsCreated: 0,
    totalListsUpdated: 0,
  });

  // Main processing function. `sdataGroup` is passed straight through to
  // processLists() - question objects already carry rows/columns/statements
  // (or the legacy Rows/Cols/Stats) directly, no translation layer needed.
  //
  // `silent` skips both the success and error toasts - used when this runs
  // as a background reconcile (e.g. every time AllListsModal opens) rather
  // than an explicit user-triggered "Add All Lists" click.
  const addAllLists = useCallback(
    async (sdataGroup, existingLists, setList, { silent = false } = {}) => {
      setIsProcessing(true);
      setError(null);

      try {
        // Validate inputs
        if (!setList || typeof setList !== "function") {
          throw new Error("setList must be a function");
        }

        const result = processLists(sdataGroup, existingLists);

        // Update list state
        setList(result.allLists);

        // Update hook state
        setLastResult(result);
        setHistory((prev) => [
          ...prev.slice(-9), // Keep last 10 items
          {
            timestamp: new Date().toISOString(),
            summary: result.summary,
            totalLists: result.allLists.length,
          },
        ]);

        // Update stats
        setStats((prev) => ({
          totalProcessed: prev.totalProcessed + 1,
          totalListsCreated: prev.totalListsCreated + result.listsCreated,
          totalListsUpdated: prev.totalListsUpdated + result.listsUpdated,
        }));

        // Auto-save if enabled
        if (autoSave && typeof autoSave === "function") {
          try {
            await autoSave(result.allLists);
          } catch (saveError) {
            console.warn("Auto-save failed:", saveError);
          }
        }

        // Only surface a toast when there's actually something to report -
        // a background reconcile that found nothing new shouldn't interrupt.
        if (showAlerts && !silent && (result.summary.created > 0 || result.summary.updated > 0)) {
          toast.success(
            `List processing completed! ${result.summary.created} new lists created, ${result.summary.updated} updated.`
          );
        }

        if (onSuccess) onSuccess(result);
        setIsProcessing(false);
        return result;
      } catch (err) {
        const errorMessage = err.message || "An unknown error occurred";
        setError(errorMessage);

        if (showAlerts && !silent) {
          toast.error(`Error: ${errorMessage}`);
        }

        // Call error callback
        if (onError) {
          onError(err);
        }

        setIsProcessing(false);
        throw err;
      }
    },
    [showAlerts, autoSave, onSuccess, onError]
  );

  // Helper function to get processing summary
  const getSummary = useCallback(() => {
    if (!lastResult) return null;

    return {
      message: `Processed ${lastResult.summary.total} lists`,
      details: lastResult.summary,
      timestamp: new Date().toLocaleString(),
    };
  }, [lastResult]);

  // Helper to clear history
  const clearHistory = useCallback(() => {
    setHistory([]);
  }, []);

  // Helper to undo last operation (if needed)
  const undoLast = useCallback(
    (setList) => {
      if (history.length > 1 && lastResult) {
        // This would need your specific undo logic
        // For now, just a placeholder
        // console.log ('Undo functionality would go here');
      }
    },
    [history, lastResult]
  );

  // Reset hook state
  const reset = useCallback(() => {
    setIsProcessing(false);
    setLastResult(null);
    setError(null);
  }, []);

  return {
    // Main function
    addAllLists,

    // State
    isProcessing,
    lastResult,
    error,
    history,
    stats,

    // Helpers
    getSummary,
    clearHistory,
    undoLast,
    reset,

    // Convenience properties
    hasResults: !!lastResult,
    canUndo: history.length > 1,
    lastProcessedCount: lastResult?.summary?.total || 0,
  };
};

// Hook for analytics tracking
export const useListProcessorWithAnalytics = (analyticsService) => {
  const baseProcessor = useListProcessor({
    onSuccess: (result) => {
      if (analyticsService) {
        analyticsService.track("lists_processed", {
          total: result.summary.total,
          created: result.summary.created,
          updated: result.summary.updated,
        });
      }
    },
    onError: (error) => {
      if (analyticsService) {
        analyticsService.track("list_processing_error", {
          error: error.message,
        });
      }
    },
  });

  return baseProcessor;
};
